/**
 * Grid tiles from local_library_get_albums may omit cover hashes when the album
 * row has enrichment cover_art_url but no artwork_id linked to embedded/folder.
 * Detail IPC (local_library_get_album) returns the same hash columns plus
 * sample_file_path for folder fallback. This module lazily fetches those hashes
 * for visible grid tiles — one getAlbum per album per session, concurrency capped.
 */

import { libraryService } from '../services/libraryService';
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import { isLocalLibraryAlbumKey } from '../services/localLibraryMetadata';
import { normalizeCoverHash } from './coverArtIPC';
import {
  findIndexCoverHintsForAlbum,
  indexHintsHaveCoverHash,
  type LocalAlbumCoverHints,
} from './localAlbumIndexCover';

/** undefined = not yet checked; null = checked, no hash; string = 64-char hash */
const _hashByAlbum = new Map<string, string | null>();
const _inflight = new Map<string, Promise<string | null>>();
const _listeners = new Map<string, Set<() => void>>();

const MAX_CONCURRENT = 5;
let activeCount = 0;
const waitQueue: Array<() => void> = [];

function albumKeyNorm(raw: string | null | undefined): string {
  return String(raw || '').trim();
}

function notify(albumKey: string) {
  const subs = _listeners.get(albumKey);
  if (!subs) return;
  for (const fn of subs) fn();
}

function runPooled(task: () => Promise<void>) {
  const run = () => {
    activeCount += 1;
    task().finally(() => {
      activeCount -= 1;
      const next = waitQueue.shift();
      if (next) next();
    });
  };
  if (activeCount < MAX_CONCURRENT) run();
  else waitQueue.push(run);
}

function hashFromHints(hints?: LocalAlbumCoverHints | null): string | null {
  if (!hints) return null;
  return normalizeCoverHash(hints.embedded_cover_hash)
    || normalizeCoverHash(hints.folder_cover_hash)
    || normalizeCoverHash(hints.external_cover_hash)
    || null;
}

/**
 * Pre-seed session cover hash from index / navigation — avoids getAlbum round-trip
 * and prevents a failed lookup from marking the album artless when the wall has art.
 */
export function seedIndexCoverHashes(
  albumKey: string | null | undefined,
  hints?: LocalAlbumCoverHints | null,
): void {
  const key = albumKeyNorm(albumKey);
  if (!key) return;
  const hash = hashFromHints(hints);
  if (!hash) return;
  if (_hashByAlbum.has(key) && _hashByAlbum.get(key)) return;
  _hashByAlbum.set(key, hash);
  notify(key);
}

async function fetchDetailCoverHash(
  albumKey: string,
  indexHints?: LocalAlbumCoverHints | null,
): Promise<string | null> {
  const key = albumKeyNorm(albumKey);
  if (_hashByAlbum.has(key)) return _hashByAlbum.get(key) ?? null;

  const seeded = hashFromHints(indexHints);
  if (seeded) return seeded;

  const existing = _inflight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<string | null> => {
    try {
      if (shouldUseLocalLibraryIpc() && isLocalLibraryAlbumKey(key)) {
        const resp = await libraryService.getAlbum(key);
        const album = (resp as { album?: Record<string, unknown> })?.album;
        if (!album) {
          const fallback = hashFromHints(findIndexCoverHintsForAlbum(key));
          return fallback;
        }
        return normalizeCoverHash(album.embedded_cover_hash as string)
          || normalizeCoverHash(album.folder_cover_hash as string)
          || normalizeCoverHash(album.external_cover_hash as string)
          || hashFromHints(findIndexCoverHintsForAlbum(key))
          || null;
      }
      const resp = await libraryService.getAlbum(key);
      const album = (resp as { album?: Record<string, unknown> })?.album;
      if (!album) return null;
      return normalizeCoverHash(album.embedded_cover_hash as string)
        || normalizeCoverHash(album.folder_cover_hash as string)
        || null;
    } catch {
      return hashFromHints(findIndexCoverHintsForAlbum(key));
    } finally {
      _inflight.delete(key);
    }
  })();

  _inflight.set(key, promise);
  return promise;
}

/** True when detail IPC confirmed this album has a cover hash this session. */
export function hasExternalDetailHash(albumKey: string | null | undefined): boolean {
  const key = albumKeyNorm(albumKey);
  if (!key) return false;
  const h = _hashByAlbum.get(key);
  return typeof h === 'string' && h.length > 0;
}

/** True when getAlbum ran and returned no cover hash — skip repeat IPC. */
export function isExternalCoverKnownArtless(albumKey: string | null | undefined): boolean {
  const key = albumKeyNorm(albumKey);
  if (!key) return false;
  return _hashByAlbum.has(key) && _hashByAlbum.get(key) == null;
}

/** Session lookup — undefined if not yet fetched. */
export function getExternalDetailHash(
  albumKey: string | null | undefined,
): string | null | undefined {
  const key = albumKeyNorm(albumKey);
  if (!key || !_hashByAlbum.has(key)) return undefined;
  return _hashByAlbum.get(key) ?? null;
}

export function subscribeExternalCoverResolve(
  albumKey: string | null | undefined,
  listener: () => void,
): () => void {
  const key = albumKeyNorm(albumKey);
  if (!key) return () => {};
  let set = _listeners.get(key);
  if (!set) {
    set = new Set();
    _listeners.set(key, set);
  }
  set.add(listener);
  return () => { set!.delete(listener); };
}

export function clearExternalAlbumCoverCache(): void {
  _hashByAlbum.clear();
  _inflight.clear();
  for (const subs of _listeners.values()) {
    for (const fn of subs) fn();
  }
}

/**
 * Queue a detail IPC lookup for one grid tile. Visible tiles only — call from
 * mounted AlbumCard instances (virtual scroll already limits mount count).
 */
export function requestExternalAlbumCoverHash(
  albumKey: string | null | undefined,
): void {
  if (!shouldUseLocalLibraryIpc()) return;
  const key = albumKeyNorm(albumKey);
  if (!key || !isLocalLibraryAlbumKey(key)) return;

  const indexHints = findIndexCoverHintsForAlbum(key);
  seedIndexCoverHashes(key, indexHints);
  if (_hashByAlbum.has(key)) return;
  if (_inflight.has(key)) return;

  runPooled(async () => {
    if (_hashByAlbum.has(key)) return;

    const hash = await fetchDetailCoverHash(key, indexHints);
    if (hash) {
      _hashByAlbum.set(key, hash);
      import('../hooks/useLibraryImageEnrichment').then(({ applyAlbumCoverOverlay }) => {
        applyAlbumCoverOverlay(key, { embedded_cover_hash: hash });
      }).catch(() => {});
    } else if (!indexHintsHaveCoverHash(indexHints)) {
      _hashByAlbum.set(key, null);
    }

    notify(key);
  });
}
